import React, { useEffect, useState } from "react"
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, Tooltip, Typography } from "@mui/material"
import { dsColorNew } from '@palette'
import { AutocompleteAjaxSeeker, IconSeeker, NumberFieldSeeker } from "@ui-seeker/core"
import { useValidatorSeeker } from "@app"
import { object, string } from "yup"
import { BACKEND_APIS } from "@constants"
import { dsRepricingDrawerData, dsRepricingDrawerErrors } from "Pages/DsRepricing/Page/@signals"

const labelsFormatted = {
	0: { icon: "money-bills", label: "Currency", color: dsColorNew.successText },
	1: { icon: "percentage", label: "Percentage", color: "none" },
}

export default function DsRepricingUndercut() {
	const initial = dsRepricingDrawerData.peek().undercut
	const [undercutValue, setUndercutValue] = useState(initial?.value ?? null)
	const [undercutType, setUndercutType] = useState(initial?.type ?? 1)
	const [undercutDirection, setUndercutDirection] = useState(initial?.direction ?? 0)

	const isPercentage = undercutType === 1

	// Reaching a target position means undercutting whoever holds it, so an upward offset would
	// contradict the target: it would land one position worse. Ranking forces direction Down.
	const isRanking = dsRepricingDrawerData.value.strategy?.type === 'ranking'

	// 0 is only meaningful with Market Trend, where it means "sit exactly on the market average".
	// With Ranking it would just tie with the competitor holding the target position.
	const max = isPercentage ? 100 : 500
	const min = isRanking ? 0.1 : 0
	// Required only with Ranking, where an empty offset would tie with the competitor holding the
	// target position instead of taking it. With Market Trend an empty offset is a real answer:
	// sit exactly on the market average.
	const blank = v => v === null || v === undefined || v === ""
	const schema = object({
		undercutValue: isRanking
			? string()
				.required("Undercut is required")
				.test("range", `Must be between ${min} and ${max}`,
					v => !blank(v) && Number(v) >= min && Number(v) <= max)
			: string()
				.nullable()
				.test("range", `Must be between ${min} and ${max}`,
					v => blank(v) || (Number(v) >= min && Number(v) <= max)),
	})

	const { errors, isValid } = useValidatorSeeker({ undercutValue }, schema)

	useEffect(() => {
		dsRepricingDrawerErrors.value = { ...dsRepricingDrawerErrors.value, undercutValue: isValid }
	}, [JSON.stringify(errors)])

	useEffect(() => () => {
		dsRepricingDrawerErrors.value = { ...dsRepricingDrawerErrors.peek(), undercutValue: undefined }
	}, [])

	// Coerce whenever ranking becomes active: on strategy switch and on loading an existing rule.
	useEffect(() => {
		if (!isRanking) return
		const current = dsRepricingDrawerData.value.undercut
		if (current?.direction !== 1) return
		setUndercutDirection(0)
		dsRepricingDrawerData.value = {
			...dsRepricingDrawerData.value,
			undercut: { ...current, direction: 0 },
		}
	}, [isRanking])

	// The undercut is a single offset for the whole rule: there are no per-provider overrides here, so
	// a rule loaded with some (they were configurable until 2026-07-30) drops them on the next save.
	const updateSignal = (valueOverride, typeOverride, directionOverride) => {
		dsRepricingDrawerData.value = {
			...dsRepricingDrawerData.value,
			undercut: {
				value: valueOverride !== undefined ? valueOverride : undercutValue,
				type: typeOverride !== undefined ? typeOverride : undercutType,
				direction: directionOverride !== undefined ? directionOverride : undercutDirection,
			},
		}
	}

	const handleValueChange = (e) => {
		setUndercutValue(e.target.value)
		updateSignal(e.target.value, undefined, undefined)
	}

	const handleTypeChange = (e) => {
		setUndercutType(e.target.value)
		updateSignal(undefined, e.target.value, undefined)
	}

	const handleDirectionChange = (e) => {
		setUndercutDirection(e.target.value)
		updateSignal(undefined, undefined, e.target.value)
	}

	return (
		<Grid container spacing={2} alignItems="flex-start">
			<Grid item xs={5}>
				<NumberFieldSeeker
					label="Undercut"
					placeholder="0"
					value={undercutValue ?? ""}
					onChange={handleValueChange}
					error={errors?.undercutValue}
				/>
			</Grid>
			<Grid item xs={3}>
				<AutocompleteAjaxSeeker
					filterSelectedOptions={false}
					disableClearable
					urlAjax={`${BACKEND_APIS.MASTER_DATA}/all/options/comparator_unit`}
					label="Select unit"
					inputProps={{
						startAdornment: <IconSeeker
							icon={labelsFormatted[undercutType]?.icon}
							sx={{ color: labelsFormatted[undercutType]?.color, ml: 1, mr: .5 }}
						/>
					}}
					onAjax={options => options.map(({ value }) => ({
						value,
						label: labelsFormatted[value].label,
					}))}
					formatOptionLabel={({ value }) => (
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
							<IconSeeker icon={labelsFormatted[value].icon} sx={{ color: labelsFormatted[value].color }}/>
							<Typography sx={{ fontSize: 14 }}>{labelsFormatted[value].label}</Typography>
						</Box>
					)}
					placeholder="Unit"
					value={undercutType}
					onChange={handleTypeChange}
				/>
			</Grid>
			<Grid item xs={4}>
				<Tooltip title={isRanking ? "Ranking always undercuts the competitor holding the target position, so the offset can only go down" : ""}>
					<FormControl size="small" fullWidth disabled={isRanking}>
						<InputLabel>Direction</InputLabel>
						<Select
							label="Direction"
							value={isRanking ? 0 : undercutDirection}
							onChange={handleDirectionChange}
						>
							<MenuItem value={0}><IconSeeker icon="arrow-down" sx={{ fontSize: 12, mr: 1, color: dsColorNew.textMuted }}/>Down</MenuItem>
							<MenuItem value={1} disabled={isRanking}><IconSeeker icon="arrow-up" sx={{ fontSize: 12, mr: 1, color: dsColorNew.textMuted }}/>Up</MenuItem>
						</Select>
					</FormControl>
				</Tooltip>
			</Grid>
		</Grid>
	)
}